import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { loadCurrentCycleContents } from '../../services/admin/admin-current-cycle-content.service';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import TocIcon from '@mui/icons-material/Toc';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvCurrentCycleContentList from '../UI/organisms/OvCurrentCycleContentList';

const ADMIN_CURRENT_CYCLE_CONTENTS_LIMIT = 10;

const CurrentCycleContents = () => {
  const { t } = useTranslation();
  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const query = queryString.stringify({
    limit: ADMIN_CURRENT_CYCLE_CONTENTS_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
  });

  const {
    data: paginatedCurrentCycleContents,
    isPending: isGetCurrentCycleContentsPending,
    isFetching: isGetCurrentCycleContentsFetching,
    error: getCurrentCycleContentsError,
  } = useQuery({
    queryKey: ['admin-current-cycle-contents', query],
    queryFn: () => loadCurrentCycleContents(query),
    retry: 3,
    placeholderData: keepPreviousData,
  });

  const currentCycleContents = paginatedCurrentCycleContents?.docs ?? [];
  const total = paginatedCurrentCycleContents?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('current_phase');
      setOrderDir('asc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const isLoading =
    isGetCurrentCycleContentsPending || isGetCurrentCycleContentsFetching;

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * ADMIN_CURRENT_CYCLE_CONTENTS_LIMIT);
  };

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <OvPageTitle
            title={t('common.currentCycleContents')}
            icon={<StyledTocIcon />}
          />
        </HeaderItems>
        <ActionBar>
          <OvPaginationBar
            total={total}
            limit={ADMIN_CURRENT_CYCLE_CONTENTS_LIMIT}
            offset={offset}
            onChange={goToPage}
          />
        </ActionBar>

        {getCurrentCycleContentsError && (
          <ErrorMessageWrapper>
            <OvErrorMessage message={getCurrentCycleContentsError?.message} />
          </ErrorMessageWrapper>
        )}

        {currentCycleContents?.length ? (
          <ContentWrapper>
            <OvCurrentCycleContentList
              currentCycleContents={currentCycleContents}
            />
          </ContentWrapper>
        ) : !isLoading ? (
          <OvNoContent>{t('common.empty')}</OvNoContent>
        ) : null}

        {isLoading && <OvLoadingIndicator position="fixed" />}
      </CenterPane>
    </Container>
  );
};

export default CurrentCycleContents;

const Container = styled.div`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
  margin-left: 1rem;
  background-color: ${Colours.WHITE};
`;

const HeaderItems = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  justify-content: flex-start;
  gap: 1rem;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const StyledTocIcon = styled(TocIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const ErrorMessageWrapper = styled.div`
  margin: 1rem 0;
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;
